import { fetchBaseQuery, FetchArgs, BaseQueryFn } from '@reduxjs/toolkit/query';
import type { RootState } from '..';
import { logout } from './auth/authSlice';
import { forceMaintenanceCheck, setMaintenanceMode } from '../slices/maintenanceSlice';
import { setRateLimited } from '../slices/rateLimitSlice';
import { toast } from 'react-toastify';
import logger from 'utils/logger';
import { isInlineHandled429 } from 'utils/inlineHandled429';

// Public pages where 401 errors should NOT trigger a redirect to login
// These pages are accessible without authentication and should remain accessible
const PUBLIC_PAGES = ['/faq', '/compare', '/sets', '/articles', '/search', '/terms', '/privacy', '/collection/', '/profile/', '/u/', '/docs/', '/elite', '/eternaldurdles', '/cards/', '/brand', '/topdecked', '/affiliates', '/legal/'];

// Safely get API URL with validation
let API_BASE_URL = 'http://localhost:4000/api/v1';
try {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.length > 0) {
    // Validate URL format
    if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
      API_BASE_URL = envUrl;
    } else {
      console.error('Invalid API URL format:', envUrl);
    }
  }
} catch (error) {
  console.error('Error parsing API URL:', error);
}

// Check if cookie auth is enabled at module level
let USE_COOKIE_AUTH_GLOBAL = false;
try {
  USE_COOKIE_AUTH_GLOBAL = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';
} catch (error) {
  console.error('Error parsing global USE_COOKIE_AUTH:', error);
}

// Debug logging to identify staging issues
console.log('📍 API Base URL:', API_BASE_URL);
console.log('🔄 Build timestamp:', Date.now());
console.log('🔧 Environment check:', {
  api_url: process.env.REACT_APP_API_URL,
  cookie_auth: process.env.REACT_APP_USE_COOKIE_AUTH,
  env: process.env.REACT_APP_ENV,
  node_env: process.env.NODE_ENV
});
console.log('🍪 Cookie Auth Mode:', USE_COOKIE_AUTH_GLOBAL ? 'ENABLED (credentials: include)' : 'DISABLED (credentials: same-origin)');

// Additional staging debug info
if (process.env.REACT_APP_ENV === 'staging') {
  console.log('🚀 Staging Environment Detected');
  console.log('🔐 Cookie Details:', {
    hasCookies: typeof document !== 'undefined' && document.cookie.length > 0,
    cookieList: typeof document !== 'undefined' ? document.cookie.split(';').map(c => c.trim().split('=')[0]) : [],
  });
}

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  // Extract custom timeout from args if provided (for long-running operations like bulk imports)
  const customTimeout = typeof args === 'object' && args !== null && 'timeout' in args
    ? (args as any).timeout
    : undefined;

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    timeout: customTimeout || 60000, // Use custom timeout or default 60 seconds
    credentials: USE_COOKIE_AUTH_GLOBAL ? 'include' : 'same-origin', // Only include cookies if enabled
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth?.token;

      // Get the HTTP method
      const method = typeof args === 'object' && args !== null && 'method' in args
        ? (args as any).method?.toUpperCase()
        : 'GET';

      // PRIORITY 1: Impersonation mode - takes precedence over everything
      const isImpersonating = typeof window !== 'undefined' && localStorage.getItem('isImpersonating') === 'true';

      if (isImpersonating && token) {
        headers.set('Authorization', `Bearer ${token}`);

        // Add CSRF for state-changing requests during impersonation
        const csrfToken = state.auth?.csrfToken;
        if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
          headers.set('X-CSRF-Token', csrfToken);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🎭 IMPERSONATION MODE: Sending Bearer token');
        }

        return headers;  // ← EARLY RETURN - Skip all other auth logic
      }

      // Log if impersonation was expected but token missing
      if (isImpersonating && !token) {
        console.error('⚠️ Impersonation mode active but no token in Redux state!');
        console.error('   This is a bug - token should have been stored by authSlice');
      }

      // Check if this request requires auth
      let requiresAuth = false;
      if (typeof args === 'object' && args !== null && 'requiresAuth' in args) {
        requiresAuth = (args as any).requiresAuth;
      }

      // Add CSRF token for state-changing requests
      const csrfToken = state.auth?.csrfToken;
      if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        headers.set('X-CSRF-Token', csrfToken);
      }

      if (requiresAuth) {
        let USE_COOKIE_AUTH = false;
        try {
          USE_COOKIE_AUTH = USE_COOKIE_AUTH_GLOBAL || process.env.REACT_APP_USE_COOKIE_AUTH === 'true';
        } catch (error) {
          console.error('Error parsing USE_COOKIE_AUTH in baseQuery:', error);
        }

        if (!USE_COOKIE_AUTH) {
          // Token auth mode
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
        } else {
          // Cookie auth mode
          if (process.env.NODE_ENV === 'development') {
            console.log('🍪 Using cookie authentication - token in HttpOnly cookie');
          }
        }
      }

      return headers;
    },
  });

  // TIMING AUDIT: Track API response times to identify slow operations
  // This is temporary logging to help identify which endpoints need longer timeouts
  const startTime = performance.now();
  const endpoint = typeof args === 'string' ? args : (args as any).url;
  const method = typeof args === 'object' && args !== null && 'method' in args
    ? (args as any).method?.toUpperCase()
    : 'GET';

  // Remove custom flags (requiresAuth, timeout) before sending the request
  let result;
  if (typeof args === 'object' && args !== null && ('requiresAuth' in args || 'timeout' in args)) {
    const { requiresAuth, timeout, ...cleanArgs } = args as any;
    result = await rawBaseQuery(cleanArgs, api, extraOptions);
  } else {
    result = await rawBaseQuery(args, api, extraOptions);
  }

  // TIMING AUDIT: Log slow operations (>10 seconds)
  const duration = performance.now() - startTime;
  if (duration > 10000) {
    logger.warn('Slow API operation detected', {
      endpoint,
      method,
      duration: `${(duration / 1000).toFixed(2)}s`,
      exceeds10s: true,
      timestamp: new Date().toISOString(),
    });
  } else if (duration > 5000) {
    // Also log operations >5s for awareness
    logger.debug(`API operation took ${(duration / 1000).toFixed(2)}s`, {
      endpoint,
      method,
    });
  }

  // Handle 401 errors by clearing the token and logging out
  if (result.error && (result.error as any).status === 401) {
    // Check if this endpoint requires auth
    const requiresAuth = typeof args === 'object' && args !== null && 'requiresAuth' in args
      ? (args as any).requiresAuth
      : true; // Default to true for backwards compatibility

    // Only logout and redirect if this endpoint requires authentication
    if (requiresAuth) {
      // Extract the endpoint URL for debugging
      const endpointUrl = typeof args === 'string' ? args : (args as any).url;

      console.error('🔴 401 LOGOUT TRIGGER - DEBUG INFO:', {
        endpoint: endpointUrl,
        method: typeof args === 'object' ? (args as any).method : 'GET',
        fullArgs: args,
        error: result.error,
        errorData: (result.error as any).data,
        currentPath: window.location.pathname,
        hasToken: !!(api.getState() as RootState).auth?.token,
        hasUser: !!localStorage.getItem('user'),
        timestamp: new Date().toISOString()
      });

      // Special handling for /trades/active endpoint - don't logout immediately
      if (endpointUrl && endpointUrl.includes('/trades/active')) {
        console.warn('⚠️ 401 on trades endpoint - Backend says "Token is invalid or expired" for some users');
        console.warn('⚠️ Not logging out automatically for this specific endpoint');
        // Still return the error so the component can handle it
        return result;
      }

      console.error('🔴 401 Unauthorized - Token is invalid or expired');

      // Clear the invalid token from state and localStorage
      api.dispatch(logout());

      // Redirect to login page if not already there and not on a public page
      const currentPath = window.location.pathname;
      const isOnPublicPage = PUBLIC_PAGES.some(path => currentPath.startsWith(path));

      if (!currentPath.includes('/login') && !currentPath.includes('/auth') && !isOnPublicPage) {
        window.location.href = '/login';
      }
    } else {
      // Public endpoint returning 401 - just log it but don't logout/redirect
      const endpointUrl = typeof args === 'string' ? args : (args as any).url;
      console.warn('⚠️ 401 on public endpoint - not redirecting:', {
        endpoint: endpointUrl,
        error: (result.error as any).data
      });
    }
  }

  // Handle 403 Forbidden errors - check if user is banned
  if (result.error && (result.error as any).status === 403) {
    const errorData = (result.error as any).data;

    // Check if this is an ACCOUNT_BANNED error from backend
    if (errorData?.error === 'ACCOUNT_BANNED') {
      console.error('🚫 ACCOUNT BANNED - User has been banned by admin');
      console.error('Ban reason:', errorData?.banReason || errorData?.message);

      // Show user-friendly error message
      alert(`Your account has been suspended.\n\n${errorData?.message || 'Please contact support for more information.'}`);

      // Clear authentication state
      api.dispatch(logout());

      // Redirect to login page
      window.location.href = '/welcome';
    }
  }

  // Handle 429 Rate Limit errors.
  // Skipped for the few coded 429s whose own surface renders the copy inline
  // (see utils/inlineHandled429): no toast, no polling pause for those.
  if (
    result.error &&
    (result.error as any).status === 429 &&
    !isInlineHandled429((result.error as any).data)
  ) {
    const errorData = (result.error as any).data;
    const endpoint = typeof args === 'string' ? args : (args as any).url;

    // Parse new backend format or fall back to legacy Retry-After header
    const retryAfter = errorData?.retryAfter || 900; // Default to 15 minutes
    const limit = errorData?.limit;
    const window = errorData?.window || '15 minutes';

    console.warn('⏱️ RATE LIMIT EXCEEDED:', {
      endpoint,
      retryAfter,
      limit,
      window,
      message: errorData?.message,
    });

    // Calculate human-readable wait time
    const minutes = Math.ceil(retryAfter / 60);
    const waitTimeText = minutes >= 60
      ? `${Math.ceil(minutes / 60)} hour${Math.ceil(minutes / 60) > 1 ? 's' : ''}`
      : `${minutes} minute${minutes > 1 ? 's' : ''}`;

    // Store rate limit state in Redux (for polling pause/resume)
    api.dispatch(setRateLimited({
      retryAfter,
      endpoint,
      limit,
      window,
    }));

    // Show user-friendly toast notification
    toast.error(
      `You're making requests too quickly. Please wait ${waitTimeText} before trying again.`,
      { autoClose: 8000 } // Show for 8 seconds
    );

    // Schedule automatic clearing of rate limit state
    setTimeout(() => {
      api.dispatch({ type: 'rateLimit/clearRateLimited' });
    }, retryAfter * 1000);
  }

  // Handle 503 Maintenance Mode errors
  if (result.error && (result.error as any).status === 503) {
    const errorData = (result.error as any).data;

    // Check if this is a MAINTENANCE_MODE error from backend
    if (errorData?.error === 'MAINTENANCE_MODE') {
      console.warn('🚧 MAINTENANCE MODE DETECTED - Site is under maintenance');
      console.warn('Message:', errorData?.message);

      // Store maintenance mode active state in Redux
      // This allows non-admin users to be blocked without querying the admin endpoint
      api.dispatch(setMaintenanceMode(true));

      // Dispatch action to force immediate maintenance check
      // This will trigger the MaintenanceGuard to re-check and show maintenance page
      api.dispatch(forceMaintenanceCheck());
    }
  }

  // Log other errors
  if (result.error) {
    const errorData = (result.error as any).data;
    const errorStatus = (result.error as any).status;

    // Enhanced staging error logging
    if (process.env.REACT_APP_ENV === 'staging') {
      console.error('🔴 Staging API Error:', {
        endpoint: typeof args === 'string' ? args : (args as any).url,
        status: errorStatus,
        error: errorData?.error || errorData?.message || 'Unknown error',
        details: errorData,
        cookies: document.cookie.split(';').map(c => c.trim().split('=')[0]),
        hasAuthCookie: document.cookie.includes('auth-token'),
        hasUser: !!localStorage.getItem('user'),
      });
    } else if (process.env.NODE_ENV !== 'production') {
      console.error('🔴 API Error Response:', {
        args,
        error: result.error,
        status: errorStatus,
        data: errorData,
        message: errorData?.message || errorData?.error || 'Unknown error',
      });
    } else {
      // Production: strings only — consoles retain logged object references,
      // and per-request payload logging was the demonstrated OOM allocator on
      // /sendcards (Sentry JAVASCRIPT-REACT-14).
      console.error(
        '🔴 API Error:',
        errorStatus,
        typeof args === 'string' ? args : (args as any).url,
        errorData?.message || errorData?.error || 'Unknown error'
      );
    }
  } else if (process.env.NODE_ENV !== 'production') {
    console.log('🟢 API Success Response:', {
      args,
      data: result.data,
    });
  }

  return result;
};
